/**
 * MSAL Authentication Configuration Module
 *
 * Initializes and manages the Microsoft Authentication Library (MSAL) for Azure AD SSO.
 * Configuration is loaded from ConfigContext, which composes auth values from
 * src/configs/[environment]/envConfigs/environment.yml and Vite build env.
 *
 * Exports a module-level singleton instance to prevent re-initialization.
 *
 * Configuration Structure:
 * - authentication.azure.clientId: Azure AD application (client) ID
 * - authentication.azure.tenantId: Azure AD tenant ID
 * - authentication.azure.redirectUri: OAuth redirect URI
 * - authentication.azure.scopes: OAuth scopes to request
 *
 * Usage:
 *   import { createMsalConfig, getMsalInstance } from '@/authConfig'
 *   const msalConfig = createMsalConfig(configFromYaml)
 *   const instance = getMsalInstance(configFromYaml)
 *   const result = await instance.loginPopup({ scopes: ['User.Read'] })
 */

import { PublicClientApplication } from '@azure/msal-browser'
import type { Configuration } from '@azure/msal-browser'

// ============================================================================
// Configuration Types
// ============================================================================

export interface AzureAuthConfig {
  clientId: string
  tenantId: string
  redirectUri: string
  scopes?: string[]
  cacheLocation?: 'sessionStorage' | 'localStorage'
  storeAuthStateInCookie?: boolean
}

export interface AuthConfigYaml {
  authentication?: {
    azure?: AzureAuthConfig
  }
  [key: string]: any
}

// ============================================================================
// MSAL Configuration Factory
// ============================================================================

/**
 * Create MSAL PublicClientApplication configuration from auth config
 *
 * @param config - Configuration object with authentication.azure properties
 * @returns MSAL Configuration object
 * @throws Error if required Azure AD settings are missing
 */
export function createMsalConfig(config?: AuthConfigYaml): Configuration {
  // Extract Azure configuration from config parameter
  const azureConfig = config?.authentication?.azure

  // Validate required configuration
  if (!azureConfig?.clientId) {
    throw new Error(
      'Azure AD configuration missing: clientId required. ' +
        'Check environment.yml (VITE_CLIENT_ID) configuration.'
    )
  }

  if (!azureConfig?.tenantId) {
    throw new Error(
      'Azure AD configuration missing: tenantId required. ' +
        'Check environment.yml (VITE_TENANT_ID) configuration.'
    )
  }

  if (!azureConfig?.redirectUri) {
    throw new Error(
      'Azure AD configuration missing: redirectUri required. ' +
        'Check environment.yml (VITE_REDIRECT_URI) configuration.'
    )
  }

  const redirectUri = azureConfig.redirectUri
  const cacheLocation = (azureConfig.cacheLocation || 'sessionStorage') as
    | 'sessionStorage'
    | 'localStorage'

  return {
    auth: {
      clientId: azureConfig.clientId,
      authority: `https://login.microsoftonline.com/${azureConfig.tenantId}`,
      redirectUri,
    },
    cache: {
      cacheLocation, // Clears on tab close if sessionStorage
    },
    system: {
      loggerOptions: {
        loggerCallback: () => {
          // Suppress MSAL logging in console
        },
      },
    },
  }
}

// ============================================================================
// MSAL Instance - Module-Level Singleton
// ============================================================================

/**
 * Module-level singleton MSAL instance
 *
 * This is created once and reused across the entire application.
 * Prevents duplicate initialization which can cause issues.
 */
let msalInstance: PublicClientApplication | null = null

/**
 * Get or create the MSAL instance (singleton pattern)
 *
 * @param config - Configuration object with authentication.azure properties
 * @returns PublicClientApplication instance
 */
export function getMsalInstance(config?: AuthConfigYaml): PublicClientApplication {
  if (!msalInstance) {
    const msalConfig = createMsalConfig(config)

    msalInstance = new PublicClientApplication(msalConfig)
  }
  return msalInstance
}

/**
 * Reset MSAL instance (for testing purposes only)
 */
export function resetMsalInstance(): void {
  msalInstance = null
}

// ============================================================================
// Login Request Configuration
// ============================================================================

/**
 * Build login request scopes from ConfigContext auth config.
 *
 * Reads `authentication.azure.scopes` from the provided config.
 * Throws if scopes are missing to avoid hardcoded contract/auth fallbacks.
 */
export function buildLoginRequest(authConfig?: AuthConfigYaml): { scopes: string[] } {
  const configScopes = authConfig?.authentication?.azure?.scopes
  if (configScopes && configScopes.length > 0) {
    return { scopes: configScopes }
  }
  throw new Error(
    'Azure AD configuration missing: scopes required. ' +
      'Check environment.yml (VITE_AUTH_SCOPES) configuration.'
  )
}

// ============================================================================
// Exports for Backward Compatibility
// ============================================================================

// Export for backward compatibility with existing code
// Note: msalInstance must be initialized with getMsalInstance() after config is loaded
export { msalInstance }
